import { useState, useEffect } from 'react';
import { Plus, Trash2, AlertCircle, RefreshCw, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';

const MODES = [
  { id: 'not_set', label: 'Not set yet', hint: 'Pick a structure when contract terms are known' },
  { id: 'advance_full', label: '100% Advance', hint: 'Full contract value paid upfront' },
  { id: 'advance_plus_monthly', label: 'Advance + Monthly Retainer', hint: 'e.g. 30% advance, balance spread monthly' },
  { id: 'advance_plus_quarterly', label: 'Advance + Quarterly Retainer', hint: 'e.g. 30% advance, balance spread quarterly' },
  { id: 'monthly_retainer', label: 'Monthly Retainer', hint: 'annual_value / 12 every month' },
  { id: 'quarterly_retainer', label: 'Quarterly Retainer', hint: 'annual_value / 4 every quarter' },
  { id: 'pdc', label: 'PDC (Post-dated Cheques)', hint: 'Enter each cheque manually' },
  { id: 'milestone', label: 'Milestone-based', hint: 'Deliverable-linked payments' },
  { id: 'custom', label: 'Custom (mixed NEFT/UPI/cheque)', hint: 'Manual rows, any date + amount' },
];

const MONTHS = [
  { v: 1, l: 'Jan' }, { v: 2, l: 'Feb' }, { v: 3, l: 'Mar' }, { v: 4, l: 'Apr' },
  { v: 5, l: 'May' }, { v: 6, l: 'Jun' }, { v: 7, l: 'Jul' }, { v: 8, l: 'Aug' },
  { v: 9, l: 'Sep' }, { v: 10, l: 'Oct' }, { v: 11, l: 'Nov' }, { v: 12, l: 'Dec' },
];

const MANUAL_MODES = new Set(['pdc', 'milestone', 'custom']);

const fC = (v) => {
  const n = Number(v) || 0;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1).replace(/\.?0+$/, '')}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n.toLocaleString('en-IN')}`;
};

export default function PaymentStructureEditor({ contract, onSaved }) {
  const [mode, setMode] = useState(contract.payment_structure || 'not_set');
  const [cfg, setCfg] = useState(contract.payment_terms_config || {});
  const [saving, setSaving] = useState(false);
  const [diff, setDiff] = useState(null);

  useEffect(() => {
    // If a contract had a legacy mode (like 'monthly_billing') keep it selectable
    // but don't overwrite what the user is choosing.
    setMode(contract.payment_structure || 'not_set');
    setCfg(contract.payment_terms_config || {});
  }, [contract.id]);

  const updateCfg = (patch) => setCfg(c => ({ ...c, ...patch }));

  const saveTerms = async (regenerate) => {
    if (saving) return;
    if (regenerate && mode !== 'not_set') {
      const ok = window.confirm(
        'Regenerate schedule from these terms?\n\n' +
        'Rows already marked "received" will be preserved.\n' +
        'Future / unpaid rows will be replaced.'
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/contracts/${contract.id}/payment-terms`, {
        payment_structure: mode,
        payment_terms_config: cfg,
        regenerate: !!regenerate,
      });
      setDiff(r.data?.diff || null);
      onSaved?.();
    } catch (e) {
      alert('Failed to save payment terms: ' + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const pickedMode = MODES.find(m => m.id === mode) || MODES[0];
  const legacyMode = !MODES.some(m => m.id === mode);

  return (
    <div className="bg-[#FAFAF8] rounded-lg border p-4 space-y-4" style={{ borderColor: '#E5E1DC' }} data-testid="payment-structure-editor">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Payment Structure</p>
          <p className="text-[11px]" style={{ color: '#6B7280' }}>Define once at signing → schedule auto-generated → Mark Received flips Contracted→Realised.</p>
        </div>
      </div>

      {legacyMode && (
        <div className="flex items-start gap-2 p-2 rounded-md" style={{ backgroundColor: '#FEF3C7' }} data-testid="legacy-mode-warning">
          <AlertCircle size={14} style={{ color: '#92400E' }} className="mt-0.5 flex-shrink-0" />
          <p className="text-[11px]" style={{ color: '#92400E' }}>
            Legacy mode <strong>{contract.payment_structure}</strong> detected. Pick a standard mode and regenerate to realign the schedule.
          </p>
        </div>
      )}

      {/* Mode picker */}
      <div>
        <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Mode *</label>
        <select value={mode} onChange={e => { setMode(e.target.value); setCfg({}); }} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="payment-mode-select">
          {MODES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          {legacyMode && <option value={contract.payment_structure}>(Legacy) {contract.payment_structure}</option>}
        </select>
        <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>{pickedMode.hint}</p>
      </div>

      {/* Mode-specific fields */}
      {mode === 'advance_full' && (
        <FieldRow>
          <DateField label="Advance Date" value={cfg.advance_date || ''} onChange={v => updateCfg({ advance_date: v })} hint={`Defaults to ${contract.start_date || 'contract start'}`} />
        </FieldRow>
      )}

      {(mode === 'advance_plus_monthly' || mode === 'advance_plus_quarterly') && (
        <FieldRow>
          <NumField label="Advance %" value={cfg.advance_pct} onChange={v => updateCfg({ advance_pct: v })} min={0} max={100} testid="advance-pct-input" />
          <DateField label="Advance Date" value={cfg.advance_date || ''} onChange={v => updateCfg({ advance_date: v })} />
          {mode === 'advance_plus_monthly' && <NumField label="Retainer Day" value={cfg.retainer_day ?? 1} onChange={v => updateCfg({ retainer_day: v })} min={1} max={31} />}
          {mode === 'advance_plus_quarterly' && <SelectField label="Quarter Anchor" value={cfg.quarter_anchor_month ?? 4} onChange={v => updateCfg({ quarter_anchor_month: Number(v) })} options={MONTHS.map(m => ({ v: m.v, l: m.l }))} />}
          <BoolField label="Arrears" value={!!cfg.arrears} onChange={v => updateCfg({ arrears: v })} hint="Billed at end of period" />
          {cfg.arrears && <NumField label="Net-days" value={cfg.net_days ?? 30} onChange={v => updateCfg({ net_days: v })} min={0} max={120} />}
        </FieldRow>
      )}

      {mode === 'monthly_retainer' && (
        <FieldRow>
          <NumField label="Retainer Day (1-31)" value={cfg.retainer_day ?? 1} onChange={v => updateCfg({ retainer_day: v })} min={1} max={31} testid="retainer-day-input" />
          <BoolField label="Arrears" value={!!cfg.arrears} onChange={v => updateCfg({ arrears: v })} hint="Bill at end of month, due Net-X days later" />
          {cfg.arrears && <NumField label="Net-days" value={cfg.net_days ?? 30} onChange={v => updateCfg({ net_days: v })} min={0} max={120} />}
        </FieldRow>
      )}

      {mode === 'quarterly_retainer' && (
        <FieldRow>
          <SelectField label="Quarter starts" value={cfg.quarter_anchor_month ?? 4} onChange={v => updateCfg({ quarter_anchor_month: Number(v) })} options={MONTHS.map(m => ({ v: m.v, l: m.l }))} testid="quarter-anchor-select" />
          <BoolField label="Arrears" value={!!cfg.arrears} onChange={v => updateCfg({ arrears: v })} />
          {cfg.arrears && <NumField label="Net-days" value={cfg.net_days ?? 30} onChange={v => updateCfg({ net_days: v })} min={0} max={120} />}
        </FieldRow>
      )}

      {MANUAL_MODES.has(mode) && (
        <ManualRowsEditor
          rows={cfg.manual_rows || []}
          onChange={rows => updateCfg({ manual_rows: rows })}
          mode={mode}
        />
      )}

      {diff && (
        <div className="flex items-start gap-2 p-2 rounded-md" style={{ backgroundColor: '#ECFDF5' }} data-testid="diff-summary">
          <CheckCircle2 size={14} style={{ color: '#065F46' }} className="mt-0.5 flex-shrink-0" />
          <p className="text-[11px]" style={{ color: '#065F46' }}>
            Schedule regenerated. <strong>{diff.new_rows}</strong> rows total ({diff.preserved_received} received rows preserved, {diff.replaced_future} future rows replaced).
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap pt-2 border-t" style={{ borderColor: '#E5E1DC' }}>
        <button onClick={() => saveTerms(false)} disabled={saving} className="px-3 py-1.5 rounded-md text-xs font-medium border hover:bg-[#F7F5F2] disabled:opacity-50" style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }} data-testid="save-terms-only-btn">
          Save terms only
        </button>
        <button onClick={() => saveTerms(true)} disabled={saving || mode === 'not_set'} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#2A7B88' }} data-testid="save-and-regen-btn">
          <RefreshCw size={12} /> Save + Regenerate schedule
        </button>
      </div>

      {contract.payment_terms_audit && contract.payment_terms_audit.length > 0 && (
        <AuditTrail entries={contract.payment_terms_audit} />
      )}
    </div>
  );
}

function FieldRow({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">{children}</div>;
}

function NumField({ label, value, onChange, min, max, testid }) {
  return (
    <div>
      <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{label}</label>
      <input type="number" value={value ?? ''} min={min} max={max} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid={testid} />
    </div>
  );
}
function DateField({ label, value, onChange, hint }) {
  return (
    <div>
      <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{label}</label>
      <input type="date" value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
      {hint && <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>{hint}</p>}
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{label}</label>
      <select value={value ?? ''} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid={testid}>
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
function BoolField({ label, value, onChange, hint }) {
  return (
    <div>
      <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{label}</label>
      <label className="flex items-center gap-2 mt-1.5 text-sm" style={{ color: '#1B2A4A' }}>
        <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
        <span>{hint || 'Yes'}</span>
      </label>
    </div>
  );
}

function ManualRowsEditor({ rows, onChange, mode }) {
  const placeholder = {
    pdc: 'e.g. Cheque dated 15-Jun-26',
    milestone: 'e.g. Delivery of Module 1',
    custom: 'e.g. UPI advance / NEFT tranche 2',
  }[mode] || '';

  const addRow = () => onChange([...rows, { date: '', amount: '', label: '' }]);
  const updateRow = (idx, patch) => onChange(rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <div className="space-y-2" data-testid="manual-rows-editor">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>Manual Payment Rows</p>
        <button onClick={addRow} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border hover:bg-white" style={{ borderColor: '#E5E1DC', color: '#2A7B88' }} data-testid="add-manual-row-btn">
          <Plus size={11} /> Add row
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="text-[11px] italic" style={{ color: '#9CA3AF' }}>No rows yet. Click "Add row" for each expected payment.</p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`manual-row-${i}`}>
              <input type="date" value={r.date || ''} onChange={e => updateRow(i, { date: e.target.value })}
                className="col-span-3 px-2 py-1 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
              <input type="number" value={r.amount || ''} onChange={e => updateRow(i, { amount: e.target.value })} placeholder="Amount"
                className="col-span-3 px-2 py-1 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
              <input value={r.label || ''} onChange={e => updateRow(i, { label: e.target.value })} placeholder={placeholder}
                className="col-span-5 px-2 py-1 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
              <button onClick={() => removeRow(i)} className="col-span-1 p-1 hover:bg-red-50 rounded flex justify-center">
                <Trash2 size={12} style={{ color: '#D4726A' }} />
              </button>
            </div>
          ))}
          <p className="text-[11px] text-right pt-1" style={{ color: '#6B7280' }}>
            Total: <strong style={{ color: '#1B2A4A' }}>{fC(total)}</strong>
          </p>
        </div>
      )}
    </div>
  );
}

function AuditTrail({ entries }) {
  const sorted = [...entries].sort((a, b) => (b.changed_at || '').localeCompare(a.changed_at || ''));
  return (
    <details className="pt-2 border-t" style={{ borderColor: '#E5E1DC' }}>
      <summary className="text-[11px] cursor-pointer" style={{ color: '#6B7280' }}>Audit trail ({entries.length})</summary>
      <div className="mt-2 space-y-1.5">
        {sorted.slice(0, 10).map((e, i) => (
          <div key={i} className="text-[11px]" style={{ color: '#6B7280' }}>
            <span style={{ color: '#1B2A4A' }}>{(e.changed_at || '').slice(0, 10)}</span>
            {' · '}{e.changed_by}{' · '}{e.from_mode} → <strong style={{ color: '#1B2A4A' }}>{e.to_mode}</strong>
            {e.regenerated && <span className="ml-1 text-[10px] px-1 py-0.5 rounded" style={{ backgroundColor: '#ECFDF5', color: '#065F46' }}>regenerated</span>}
            {e.notes && <span className="italic"> — {e.notes}</span>}
          </div>
        ))}
      </div>
    </details>
  );
}
